import type { PaymentRecord, PaymentSplitResult } from "@lumora/bridge";
import { ApiError, errorMessage } from "@lumora/bridge";
import { formatUsd } from "@lumora/core";

/** Stripe-sourced hosting payments are the only ones that can be split to partners. */
export function splitEligiblePayments(payments: PaymentRecord[]): PaymentRecord[] {
  return payments.filter((p) => p.source === "stripe" && p.kind === "hosting");
}

/** One human line per partner outcome. */
export function splitResultLines(result: PaymentSplitResult): string[] {
  const out: string[] = [];
  if (result.skipped) out.push(`Split skipped: ${result.skipped}`);
  for (const l of result.lines) {
    const who = l.partnerSlug;
    switch (l.status) {
      case "done":
        out.push(
          `${who}: ${formatUsd(l.amountCents)} sent${l.transferId ? ` (transfer ${l.transferId})` : ""}`,
        );
        break;
      case "already_paid":
        out.push(`${who}: already sent`);
        break;
      case "skipped":
        out.push(`${who}: skipped: ${l.reason ?? "unknown"}`);
        break;
      default:
        out.push(`${who}: failed${l.reason ? `: ${l.reason}` : ""}`);
    }
  }
  if (out.length === 0) out.push("Nothing to send for this payment.");
  return out;
}

/** Clear text for API failures of the split endpoint. */
export function splitErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 502)
      return "Some transfers failed. It is safe to retry; shares already sent are not paid twice.";
    if (err.status === 503) return "Stripe is not configured on this server.";
    if (err.status === 400) return "This is not a Stripe hosting payment, so it cannot be split.";
  }
  return errorMessage(err, "Could not run the partner split.");
}
